// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { useCustomerConfig } from '@store';

import { Grid, useTheme } from '@mui/material';

import Spacer from '@components/Utils/Spacer';

import { ReportingMove } from '../types';
import { useReporting } from '../providers/ReportingProvider';
import {
  buildDistanceDistribution,
  buildDriverActivity,
  buildMovesByHour,
  buildMovesByWeekday,
  buildMoveVolumeByDay,
  buildOperationsMetrics,
  buildStatusDistribution,
} from '../utils/reportingMetrics';
import { formatMiles, formatNumber, formatPercent, formatRatio } from '../utils/reportingFormatters';
import { getChartColors } from '../utils/reportingChartColors';
import MetricCard from './MetricCard';
import ChartCard from './ChartCard';
import { CountBarChart, HorizontalCountBarChart, MoveVolumeChart, StatusDonutChart } from './ReportingCharts';

// TYPES ---------------------------------------------------------------- //

interface OperationsOverviewTabProps {
  moves: ReportingMove[];
  start: string;
  end: string;
  loading: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const OperationsOverviewTab = ({ moves, start, end, loading }: OperationsOverviewTabProps) => {
  const theme = useTheme();
  const colors = getChartColors(theme);
  const customerConfig = useCustomerConfig();
  const { isModuleVisible } = useReporting();
  const metricVisible = (key: string) => isModuleVisible('operations', 'metrics', key);
  const chartVisible = (key: string) => isModuleVisible('operations', 'charts', key);

  // Reference numbers may be flagged as required at either config level
  const refNumRequired =
    customerConfig?.customer_values?.ref_num_required === true || customerConfig?.ref_num_required === true;
  const refLabel = customerConfig?.reference_num_label || 'Reference Numbers';

  const metrics = React.useMemo(() => buildOperationsMetrics(moves, { refNumRequired }), [moves, refNumRequired]);

  const volumeByDay = React.useMemo(() => buildMoveVolumeByDay(moves, start, end), [moves, start, end]);
  const statusDistribution = React.useMemo(() => buildStatusDistribution(moves), [moves]);
  const movesByWeekday = React.useMemo(() => buildMovesByWeekday(moves), [moves]);
  const movesByHour = React.useMemo(() => buildMovesByHour(moves), [moves]);
  const driverActivity = React.useMemo(() => buildDriverActivity(moves), [moves]);
  const distanceDistribution = React.useMemo(() => buildDistanceDistribution(moves), [moves]);

  const hasMoves = moves.length > 0;
  const hasDistances = distanceDistribution.some(b => b.count > 0);

  return (
    <>
    {/* METRIC CARDS - separate container so cards never stretch to chart row height */}
    <Grid container spacing={2}>
      {metricVisible('total_moves') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard label='Total Moves' value={formatNumber(metrics.totalMoves)} loading={loading} />
        </Grid>
      ) : null}
      {metricVisible('completed') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard label='Completed' value={formatNumber(metrics.completed)} tone='success' loading={loading} />
        </Grid>
      ) : null}
      {metricVisible('pending') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label='Pending'
            value={formatNumber(metrics.pending)}
            subtitle='Upcoming moves not yet started.'
            loading={loading}
          />
        </Grid>
      ) : null}
      {metricVisible('canceled') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard label='Canceled' value={formatNumber(metrics.canceled)} tone='error' loading={loading} />
        </Grid>
      ) : null}

      {metricVisible('on_time_pct') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label='On-Time Percentage'
            value={formatPercent(metrics.onTimePercent)}
            subtitle={
              metrics.onTimeDenominator > 0
                ? 'Based on completed moves with delivery due times.'
                : 'No completed moves with due times.'
            }
            tooltip='Completed moves delivered at or before their delivery due time. Only completed moves with both a delivery time and a due time are counted.'
            tone={metrics.onTimePercent != null && metrics.onTimePercent >= 0.9 ? 'success' : 'default'}
            loading={loading}
          />
        </Grid>
      ) : null}
      {metricVisible('avg_distance') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard label='Average Distance' value={formatMiles(metrics.avgDistanceMiles)} loading={loading} />
        </Grid>
      ) : null}
      {metricVisible('active_drivers') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label='Active Drivers'
            value={formatNumber(metrics.activeDrivers)}
            subtitle='Distinct drivers on non-canceled moves.'
            loading={loading}
          />
        </Grid>
      ) : null}
      {metricVisible('moves_per_driver') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard label='Moves Per Driver' value={formatRatio(metrics.movesPerDriver)} loading={loading} />
        </Grid>
      ) : null}

      {metricVisible('repeat_customers') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label='Repeat Consumers'
            value={formatNumber(metrics.repeatCustomers)}
            subtitle='Car owners with more than one move in range.'
            tooltip='Distinct car owners appearing on more than one move in the date range, grouped by phone number when available, otherwise by name.'
            loading={loading}
          />
        </Grid>
      ) : null}
      {refNumRequired && metricVisible('missing_ref') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label={`Missing ${refLabel}`}
            value={formatNumber(metrics.missingRefCount)}
            tooltip={`Moves in the date range without a ${refLabel.toLowerCase()} value. Shown because your account requires ${refLabel.toLowerCase()}.`}
            tone={(metrics.missingRefCount || 0) > 0 ? 'warning' : 'default'}
            loading={loading}
          />
        </Grid>
      ) : null}
      {metricVisible('rideshares_utilized') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label='Rideshares Utilized'
            value={formatNumber(metrics.ridesharesUtilized)}
            subtitle='Moves with an active ride attached.'
            tooltip='Moves with at least one active, non-canceled ride child move, such as a return ride for the driver.'
            loading={loading}
          />
        </Grid>
      ) : null}
    </Grid>

    <Spacer size={2} />

    {/* CHARTS */}
    <Grid container spacing={2}>
      {chartVisible('move_volume') ? (
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title='Move Volume Over Time' subtitle='Daily moves by outcome' empty={!hasMoves}>
            <MoveVolumeChart data={volumeByDay} colors={colors} />
          </ChartCard>
        </Grid>
      ) : null}
      {chartVisible('status_distribution') ? (
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title='Status Distribution' subtitle='Moves by current status' empty={!hasMoves}>
            <StatusDonutChart data={statusDistribution} colors={colors} />
          </ChartCard>
        </Grid>
      ) : null}

      {chartVisible('moves_by_weekday') ? (
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title='Moves by Weekday' subtitle='Based on pickup time' empty={!hasMoves}>
            <CountBarChart data={movesByWeekday} color={colors.assigned} />
          </ChartCard>
        </Grid>
      ) : null}
      {chartVisible('moves_by_hour') ? (
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title='Moves by Hour' subtitle='Based on pickup time' empty={!hasMoves}>
            <CountBarChart data={movesByHour} color={colors.pending} />
          </ChartCard>
        </Grid>
      ) : null}

      {chartVisible('driver_activity') ? (
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title='Driver Activity'
            subtitle='Top drivers by move count'
            empty={driverActivity.length === 0}
            emptyMessage='No driver activity for this date range.'
          >
            <HorizontalCountBarChart data={driverActivity} color={colors.validated} />
          </ChartCard>
        </Grid>
      ) : null}
      {chartVisible('distance_distribution') ? (
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title='Distance Distribution'
            subtitle='Moves by lane distance'
            empty={!hasDistances}
            emptyMessage='No distance data for this date range.'
          >
            <CountBarChart data={distanceDistribution} color={colors.completed} />
          </ChartCard>
        </Grid>
      ) : null}
    </Grid>
    </>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default OperationsOverviewTab;
